import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { EventList } from './SecretKeyDetail';
import {
  KEY_STATUS_LABEL, PageHeader, Pager, ProgressBar, SecretsTabs, errMsg, fmtDateTime, useSecretFilters, useSecretsAccess,
  useUrlFilters,
} from './common';

const RISK_ORDER = ['Critical', 'High', 'Medium', 'Low', 'AWS runbook'];
const STATUS_ORDER = ['open', 'eligible', 'revoked', 'false_positive', 'wont_fix'];

function Stat({ label, value, sub, to }) {
  const body = (
    <div className="stat-card">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
  return to ? <Link to={to} style={{ textDecoration: 'none' }}>{body}</Link> : body;
}

// Per-repo counts of findings a scan had never seen before (complete, not a sample).
export function UnknownByRepo({ s, limit = 8 }) {
  const [all, setAll] = useState(false);
  const by = s?.summary_json?.unknown_by_repo;
  if (!by || !Object.keys(by).length) return null;
  const rows = Object.entries(by);
  return (
    <div style={{ marginTop: 6 }}>
      <table style={{ fontSize: 12 }}>
        <thead><tr><th>Repo</th><th>Findings</th><th>Distinct values</th><th>New keys</th><th>Auto false positive</th></tr></thead>
        <tbody>
          {(all ? rows : rows.slice(0, limit)).map(([slug, v]) => (
            <tr key={slug}>
              <td>{slug}</td><td>{v.findings}</td><td>{v.digests}</td><td>{v.new_keys}</td><td>{v.auto_false_positives}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > limit && (
        <button className="btn btn-ghost" style={{ fontSize: 12, padding: '2px 8px' }} onClick={() => setAll(!all)}>
          {all ? 'Show fewer' : `Show all ${rows.length} repos`}
        </button>
      )}
    </div>
  );
}

// last = the newest run, complete = the newest run that applied results,
// running = the run in progress (the API checks the scan lock), if any.
function ScanPanel({ last, complete, running, onTriggered }) {
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [jobId, setJobId] = useState(null);
  const { can_edit: canEdit } = useSecretsAccess();
  useEffect(() => { axios.get('/secrets/scan-runs').then((r) => setJobId(r.data.job_id)).catch(() => {}); }, []);
  const run = () => {
    if (!jobId || busy || running) return;
    setBusy(true); setMsg('');
    axios.post(`/jobs/${jobId}/trigger`)
      .then(() => { setMsg('Scan started. It takes about fifteen minutes.'); onTriggered(); })
      .catch((e) => { setMsg(errMsg(e)); onTriggered(); })
      .finally(() => setBusy(false));
  };
  const s = complete;
  const laterFailure = last && last.status === 'failed' && (!s || new Date(last.started_at) > new Date(s.started_at));
  return (
    <div className="card">
      <div className="toolbar" style={{ marginBottom: 8 }}>
        <h3 style={{ fontSize: 15 }}>Presence scan</h3>
        <span className="spacer" />
        {canEdit && jobId && <Link to={`/jobs/${jobId}`} className="btn btn-ghost">Job settings and logs</Link>}
        {canEdit && (
          <button className="btn btn-secondary" disabled={!jobId || busy || !!running} onClick={run}
            title={running ? 'Only one scan runs at a time' : 'Scan every repo now'}>
            {running ? 'Scan running…' : 'Run now'}
          </button>
        )}
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.8 }}>
        {running && (
          <div style={{ color: '#a5b4fc' }}>
            A scan is running{running.started_at ? ` since ${fmtDateTime(running.started_at)}` : ''}. Results show here when it finishes.
          </div>
        )}
        {laterFailure && (
          <div style={{ color: '#fbbf24' }}>The run at {fmtDateTime(last.started_at)} failed{last.error ? `: ${last.error}` : ''}</div>
        )}
        {s ? (
          <>
            <div>Last completed scan {fmtDateTime(s.finished_at || s.started_at)}: <strong>{s.status === 'partial' ? 'some repos could not be fetched' : 'all repos scanned'}</strong></div>
            <div>{s.repos_scanned} repos scanned, {s.repos_failed} could not be fetched.</div>
            <div>{s.keys_removed} key-in-repo pairs left current code. {s.keys_reappeared} came back.</div>
            <div>{s.new_locations} new places for known keys.</div>
            <div>
              {s.unknown_hashes} findings were values never seen before
              {s.unknown_hashes > 0 && <>: now tracked as <Link to="/secrets/keys?origin=scan">{s.new_keys || 0} new key(s)</Link>
                {s.summary_json?.auto_false_positives ? `, plus ${s.summary_json.auto_false_positives} marked false positive by rules` : ''}</>}.
            </div>
            {s.summary_json?.archived_skipped > 0 && (
              <div>{s.summary_json.archived_skipped} archived repos were skipped
                {s.summary_json.archive?.archived?.length ? `; newly archived: ${s.summary_json.archive.archived.join(', ')}` : ''}.</div>
            )}
            <UnknownByRepo s={s} />
          </>
        ) : <div style={{ color: 'var(--text-muted)' }}>No scan has completed yet.</div>}
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Scans the main branch of every repo daily at 03:00 UTC, except repos archived on Bitbucket.</div>
      </div>
      {msg && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>{msg}</div>}
    </div>
  );
}

// Team picker for the big numbers. The team lives in the URL so a filtered
// view can be bookmarked or shared.
function TeamFilter({ team, teams, onChange }) {
  return (
    <div className="card" style={{ padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      <label className="form-label" style={{ margin: 0 }} htmlFor="progress-team">Team</label>
      <select id="progress-team" className="form-input" style={{ width: 260 }} value={team} onChange={(e) => onChange(e.target.value)}>
        <option value="">All teams</option>
        {teams.map((t) => <option key={t} value={t}>{t}</option>)}
        <option value="Unknown">Unknown (owner not in directory)</option>
      </select>
      {team && <button className="btn btn-ghost" onClick={() => onChange('')}>Show all teams</button>}
      <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: '1 1 320px' }}>
        {team
          ? `Repos and folders owned by ${team}, and the keys in them. A key that is also in another team's repo counts for both teams.`
          : 'Team is the owner\'s department in the employee directory: the repo owner, or the folder owner for files under a folder with its own owner.'}
      </span>
    </div>
  );
}

export default function SecretsDashboard() {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const { get, set } = useUrlFilters();
  const team = get('team');
  const filters = useSecretFilters();
  const load = useCallback(() => {
    axios.get('/secrets/summary', { params: team ? { team } : {} })
      .then((r) => setD(r.data)).catch((e) => setError(errMsg(e)));
  }, [team]);
  useEffect(() => { load(); }, [load]);
  // While a scan runs, refresh now and then so its results appear on their own.
  const running = !!d?.scan_running;
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [running, load]);

  if (error) return <div className="page-body"><div className="error-msg">{error}</div></div>;
  if (!d) return <div className="page-body"><span className="spinner" /></div>;

  // Links from the tiles keep the team, so the list matches the number.
  const t = (to) => (team ? `${to}${to.includes('?') ? '&' : '?'}team=${encodeURIComponent(team)}` : to);
  const teams = (filters?.teams || []).filter((x) => x && x !== 'Unknown');
  const st = d.by_status;
  const total = STATUS_ORDER.reduce((a, s) => a + (st[s] || 0), 0);
  const closed = (st.revoked || 0) + (st.false_positive || 0) + (st.wont_fix || 0);

  return (
    <div>
      <PageHeader title="Secrets rotation · Progress" />
      <div className="page-body">
        <SecretsTabs />
        <TeamFilter team={team} teams={teams} onChange={(v) => set({ team: v })} />
        <div className="card-grid">
          <Stat label="Keys tracked" value={total.toLocaleString()} sub={`${d.repos.repos} repos`} to={t('/secrets/keys')} />
          <Stat label="Revoked" value={(st.revoked || 0).toLocaleString()} sub={`${closed.toLocaleString()} closed including triage`} to={t('/secrets/keys?status=revoked')} />
          <Stat label="Eligible to revoke" value={(st.eligible || 0).toLocaleString()} sub="every repo signed off" to={t('/secrets/keys?status=eligible')} />
          <Stat label="Sign-offs" value={`${(d.pairs.ready || 0).toLocaleString()}`} sub={`of ${(d.pairs.total || 0).toLocaleString()} rows: one per key per repo${d.folder_rules?.rules ? ' or owned folder' : ''}`} />
          <Stat label="Repos fully signed off" value={d.repos.fully_signed_off || 0} sub={`of ${d.repos.repos}`} to={t('/secrets/repos?done=yes')} />
          <Stat label="Keys still in code" value={(d.presence.present || 0).toLocaleString()}
            sub={`${(d.presence.in_code_at_import || 0).toLocaleString()} at the 23 Sep scan, ${(d.presence.present_fp || 0).toLocaleString()} false positives${(d.presence.present_wf || 0) ? `, ${(d.presence.present_wf || 0).toLocaleString()} won't-fix` : ''} left out`}
            to={team ? t('/secrets/keys?repo_present=yes&status=open,eligible,revoked') : '/secrets/keys?present=yes&status=open,eligible,revoked'} />
          <Stat label="Partly cleaned, not gone yet" value={(d.partial?.keys_ || 0).toLocaleString()}
            sub={`keys still in code in ${d.partial?.repos || 0} repos, though ${(d.partial?.files_cleaned || 0).toLocaleString()} of their ${((d.partial?.files_cleaned || 0) + (d.partial?.files_left || 0)).toLocaleString()} files are clean; gone once every file is`}
            to={t('/secrets/keys?progress=partial')} />
          <Stat label="Gone from code since 23 Sep" value={(d.pairs.gone || 0).toLocaleString()}
            sub={`key-in-repo pairs, ${(d.pairs.gone_ready || 0).toLocaleString()} signed off, ${(d.pairs.gone_fp || 0).toLocaleString()} of them false positives`
              + ((d.pairs.gone_archived || 0) ? `, ${d.pairs.gone_archived.toLocaleString()} by archiving ${d.pairs.repos_archived} repos` : '')} to={t('/secrets/gone')} />
          <Stat label="New since 23 Sep" value={(d.new_keys?.open_work || 0).toLocaleString()}
            sub={`keys the presence scan found after the baseline, ${(d.new_keys?.in_code || 0).toLocaleString()} still in code`
              + ((d.new_keys?.total || 0) > (d.new_keys?.open_work || 0) ? `; ${(d.new_keys.total - d.new_keys.open_work).toLocaleString()} auto false positives left out` : '')}
            to={t('/secrets/keys?origin=scan&status=open,eligible,revoked')} />
          <Stat label="Auto false positives to review" value={(d.false_positives?.awaiting || 0).toLocaleString()}
            sub={`${(d.false_positives?.auto_total || 0).toLocaleString()} by rules, ${(d.false_positives?.person_total || 0).toLocaleString()} by people · ${d.false_positives?.rules?.proposed || 0} rule(s) proposed`}
            to={t('/secrets/keys?status=false_positive&fp_marked_by=auto&fp_reviewed=no')} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(440px, 1fr))', gap: 16, marginBottom: 16 }}>
          <div className="card">
            <h3 style={{ fontSize: 15, marginBottom: 12 }}>Keys closed, by risk</h3>
            <div className="table-wrap"><table>
              <thead><tr><th>Risk</th><th>Keys</th>{STATUS_ORDER.slice(0, 3).map((s) => <th key={s}>{KEY_STATUS_LABEL[s]}</th>)}<th>Closed</th></tr></thead>
              <tbody>
                {RISK_ORDER.filter((r) => d.by_risk[r]).map((r) => {
                  const row = d.by_risk[r];
                  const n = STATUS_ORDER.reduce((a, s) => a + (row[s] || 0), 0);
                  const done = (row.revoked || 0) + (row.false_positive || 0) + (row.wont_fix || 0);
                  const riskParam = r === 'AWS runbook' ? 'none' : r;
                  return (
                    <tr key={r}>
                      <td><Link to={t(`/secrets/keys?risk=${riskParam}`)}>{r}</Link></td>
                      <td>{n}</td>
                      <td>{row.open || 0}</td><td>{row.eligible || 0}</td><td>{row.revoked || 0}</td>
                      <td><ProgressBar value={done} total={n} color="var(--success)" title={`${done} of ${n} closed`} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          </div>
          <ScanPanel last={d.last_scan} complete={d.last_complete} running={d.scan_running}
            onTriggered={() => setTimeout(load, 1500)} />
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, marginBottom: 12 }}>Sign-off by team</h3>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
            Team is the owner's department in the employee directory. Unknown means the owner is not in the active directory, often because they left.
            Click a team to show its numbers at the top.
            {d.folder_rules?.rules ? ` Folder owners override repo owners in ${d.folder_rules.repos} repo${d.folder_rules.repos === 1 ? '' : 's'} (${d.folder_rules.rules} folder rule${d.folder_rules.rules === 1 ? '' : 's'}); their keys count for the folder owner's team.` : ''}
          </p>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Team</th><th>Repos</th><th>Keys</th><th>Sign-off rows</th><th>Signed off</th><th>Still in code</th></tr></thead>
              <tbody>
                {d.by_team.map((x) => (
                  <tr key={x.team} style={x.team === team ? { background: 'var(--accent-light)' } : undefined}>
                    <td>
                      <button className="btn btn-ghost" style={{ padding: 0, textAlign: 'left' }}
                        title="Show the big numbers for this team" onClick={() => { set({ team: x.team }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                        {x.team}
                      </button>
                    </td>
                    <td><Link to={`/secrets/repos?team=${encodeURIComponent(x.team)}`}>{x.repos}</Link>
                      {x.folders ? <div style={{ fontSize: 11, color: 'var(--text-muted)' }} title="Folders this team owns inside other teams' repos">{x.folders} owned folder{x.folders === 1 ? '' : 's'}</div> : null}</td>
                    <td><Link to={`/secrets/keys?team=${encodeURIComponent(x.team)}`}>{x.keys_}</Link></td>
                    <td>{x.pairs}</td>
                    <td><div style={{ fontSize: 12 }}>{x.ready} of {x.pairs}</div><ProgressBar value={x.ready} total={x.pairs} /></td>
                    <td>{x.present}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(440px, 1fr))', gap: 16 }}>
          <div className="card">
            <h3 style={{ fontSize: 15, marginBottom: 12 }}>Owners with the most open work{team ? ` in ${team}` : ''}</h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
              Sign-off rows still in code (one per key per repo, or per owned folder), false positives left out. Folder owners are listed for their folders.
            </p>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Owner</th><th>Repos</th><th>Rows in code</th><th>Signed off</th></tr></thead>
                <tbody>
                  {d.by_owner.map((o) => (
                    <tr key={o.owner_email || 'none'}>
                      <td style={{ fontSize: 12 }}>
                        {o.owner_email ? <Link to={`/secrets/repos?owner=${encodeURIComponent(o.owner_email)}`}>{o.owner_name || o.owner_email}</Link> : 'No human owner'}
                        <div style={{ color: 'var(--text-muted)' }}>{o.owner_status}</div>
                      </td>
                      <td>{o.repos}{o.folders ? <div style={{ fontSize: 11, color: 'var(--text-muted)' }} title={o.folder_names || ''}>{o.folders} folder{o.folders === 1 ? '' : 's'}: {o.folder_names}</div> : null}</td><td>{o.pairs}</td>
                      <td><div style={{ fontSize: 12 }}>{o.ready} of {o.pairs}</div><ProgressBar value={o.ready} total={o.pairs} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SecretsActivity() {
  const { params, get, set } = useUrlFilters();
  const [data, setData] = useState({ data: [], total: 0, page: 1, limit: 100 });
  const [runs, setRuns] = useState([]);
  const [openRun, setOpenRun] = useState(null);
  const [error, setError] = useState('');
  const query = params.toString();
  const filterParams = useCallback(() => {
    const p = {};
    ['action', 'actor', 'batch_id', 'from', 'to', 'q', 'key_id', 'repo_id', 'page'].forEach((k) => { if (get(k)) p[k] = get(k); });
    p.limit = 100;
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  useEffect(() => {
    axios.get('/secrets/events', { params: filterParams() })
      .then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
  }, [filterParams]);
  useEffect(() => { axios.get('/secrets/scan-runs').then((r) => setRuns(r.data.data)).catch(() => {}); }, []);

  const exportCsv = () => {
    axios.get('/secrets/events', { params: { ...filterParams(), format: 'csv', page: undefined, limit: undefined }, responseType: 'blob' })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'secrets-audit.csv';
        a.click();
        URL.revokeObjectURL(url);
      }).catch((e) => setError(errMsg(e)));
  };

  return (
    <div>
      <PageHeader title="Secrets rotation · Activity">
        Every change to the tracker, who made it and from where. Rows are only ever added, never edited or removed.
        Times are shown in your local time zone; the CSV export uses UTC.
      </PageHeader>
      <div className="page-body">
        <SecretsTabs />
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, marginBottom: 12 }}>Presence scans</h3>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Started</th><th>Source</th><th>Status</th><th>Repos</th><th>Left code</th><th>Came back</th><th>New places</th><th>Never seen before</th><th>New keys</th><th /></tr></thead>
              <tbody>
                {runs.map((r) => (
                  <React.Fragment key={r.id}>
                  <tr>
                    <td style={{ fontSize: 12 }}>{fmtDateTime(r.started_at)}</td>
                    <td>{r.source}</td>
                    <td title={r.error || ''}>{r.status}
                      {r.status === 'failed' && r.error && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', maxWidth: 260 }}>{r.error.slice(0, 160)}</div>
                      )}
                    </td>
                    <td>{r.repos_scanned}{r.repos_failed ? ` (+${r.repos_failed} failed)` : ''}</td>
                    <td>{r.keys_removed}</td><td>{r.keys_reappeared}</td><td>{r.new_locations}</td><td>{r.unknown_hashes}</td>
                    <td>{r.new_keys ? <Link to="/secrets/keys?origin=scan">{r.new_keys}</Link> : 0}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {r.summary_json?.unknown_by_repo && Object.keys(r.summary_json.unknown_by_repo).length > 0 && (
                        <button className="btn btn-ghost" onClick={() => setOpenRun(openRun === r.id ? null : r.id)}>By repo</button>
                      )}
                      {r.batch_id && <button className="btn btn-ghost" onClick={() => set({ batch_id: r.batch_id, action: '' })}>Changes</button>}
                    </td>
                  </tr>
                  {openRun === r.id && (
                    <tr><td colSpan={10} style={{ background: 'var(--bg-elevated)' }}>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Findings never seen before, by repo (every repo, not a sample):</div>
                      <UnknownByRepo s={r} limit={20} />
                    </td></tr>
                  )}
                  </React.Fragment>
                ))}
                {!runs.length && <tr><td colSpan={10} style={{ color: 'var(--text-muted)' }}>No scans yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <div className="toolbar" style={{ alignItems: 'flex-end' }}>
            <h3 style={{ fontSize: 15, alignSelf: 'center' }}>Changes</h3>
            <span className="spacer" />
            <div style={{ width: 200 }}>
              <label className="form-label">What</label>
              <select className="form-input" value={get('action')} onChange={(e) => set({ action: e.target.value })}>
                <option value="">Everything</option>
                <option value="bulk">Bulk actions</option>
                <option value="repo_signoff">Repo sign-offs</option>
                <option value="key_status">Key status changes</option>
                <option value="key_revoker">Revoker changes</option>
                <option value="key_jira,repo_jira">Jira cards</option>
                <option value="key_search">Key searches</option>
                <option value="access_grant,access_revoke,access_denied">Access</option>
                <option value="presence_removed,presence_returned,presence_new_repo">Presence scan</option>
                <option value="repo_archived,repo_unarchived,repo_missing,repo_found">Archived or missing repos</option>
                <option value="key_analysis,key_probe">Key liveness</option>
              </select>
            </div>
            <div style={{ width: 170 }}>
              <label className="form-label">Who</label>
              <select className="form-input" value={['', 'people', 'system'].includes(get('actor')) ? get('actor') : 'custom'}
                onChange={(e) => set({ actor: e.target.value === 'custom' ? get('actor') : e.target.value })}>
                <option value="">Anyone</option>
                <option value="people">People only</option>
                <option value="system">Import and scans</option>
                {!['', 'people', 'system'].includes(get('actor')) && <option value="custom">{get('actor')}</option>}
              </select>
            </div>
            <div style={{ width: 140 }}>
              <label className="form-label">From (UTC)</label>
              <input className="form-input" type="date" value={get('from')} onChange={(e) => set({ from: e.target.value })} />
            </div>
            <div style={{ width: 140 }}>
              <label className="form-label">To (UTC)</label>
              <input className="form-input" type="date" value={get('to')} onChange={(e) => set({ to: e.target.value })} />
            </div>
            <div style={{ width: 220 }}>
              <label className="form-label">Text</label>
              <input className="form-input" defaultValue={get('q')} placeholder="Email, repo, key, note"
                onKeyDown={(e) => { if (e.key === 'Enter') set({ q: e.target.value.trim() }); }}
                onBlur={(e) => { if (e.target.value.trim() !== get('q')) set({ q: e.target.value.trim() }); }} />
            </div>
            <button className="btn btn-ghost" onClick={() => set({ action: '', actor: '', batch_id: '', from: '', to: '', q: '', key_id: '', repo_id: '' })}>Reset</button>
            <button className="btn btn-secondary" onClick={exportCsv}>Export CSV</button>
          </div>
          {get('batch_id') && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
              Showing one batch. <button className="btn btn-ghost" style={{ padding: '0 4px' }} onClick={() => set({ batch_id: '' })}>Show everything</button>
            </div>
          )}
          {error && <div className="error-msg">{error}</div>}
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>{data.total} rows</div>
          <EventList events={data.data} />
          <Pager page={data.page} limit={data.limit} total={data.total} onPage={(p) => set({ page: p }, { keepPage: true })} />
        </div>
      </div>
    </div>
  );
}
